'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowRight, CheckCircle2, Shield, Zap, TrendingUp, FileText, Users, Lock, ChevronDown, Star, Quote, Clock, AlertCircle, CreditCard, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    quote: "We cut our AP cycle from 12 days to 3. The authorization workflow alone saved us from two fraudulent invoices in the first month.",
    name: "Sandra Okafor",
    title: "Controller",
    company: "Meridian Holdings",
    initials: "SO",
    color: "bg-blue-600",
  },
  {
    quote: "As a CFO, I finally have real-time visibility into cash runway without chasing down my AP team. The policy engine is exactly what we needed.",
    name: "James Whitfield",
    title: "CFO",
    company: "Vantage Capital Group",
    initials: "JW",
    color: "bg-primary",
  },
  {
    quote: "Onboarding took 20 minutes. Our bookkeeper was processing invoices the same afternoon. The role separation is perfect for our audit requirements.",
    name: "Priya Nair",
    title: "AP Manager",
    company: "Clearstone Partners",
    initials: "PN",
    color: "bg-amber-600",
  },
];

const faqs = [
  {
    q: "How does multi-tenant isolation work?",
    a: "Every company gets a fully isolated data environment. Row-level security policies in our database ensure no tenant can ever access another's invoices, vendors, or payment data — even if they share the same infrastructure.",
  },
  {
    q: "What payment methods does LedgerPay support?",
    a: "LedgerPay processes ACH, wire transfers, and card payments through Stripe. All payment intents are created server-side with full audit trails and CFO authorization gates before any funds move.",
  },
  {
    q: "Can I set spending limits and approval thresholds?",
    a: "Yes. The Policy Engine lets CFOs define per-vendor limits, invoice amount thresholds, and dual-approval requirements. AP clerks cannot bypass these controls — they're enforced at the database level.",
  },
  {
    q: "Is LedgerPay SOC 2 compliant?",
    a: "LedgerPay is architected for SOC 2 Type II readiness with immutable audit logs, role-based access controls, encrypted data at rest and in transit, and complete payment authorization trails.",
  },
  {
    q: "How does the 14-day trial work?",
    a: "Start with full Growth plan features — no credit card required. At the end of 14 days, choose a plan or your account enters read-only mode. All your data is preserved.",
  },
  {
    q: "Can I invite my whole AP team?",
    a: "Yes. Every plan supports multiple users with distinct roles: AP Clerk, AP Manager, Controller, and CFO. Each role has scoped permissions enforced at both the UI and API layer.",
  },
];

export default function MarketingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── NAV ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/marketing" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-base text-foreground">LedgerPay</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Customers</a>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
            <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors hidden sm:block">
              Sign in
            </Link>
            <Link href="/sign-up-login-screen" className="text-sm font-semibold bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-1.5">
              Start free trial
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>
      {/* ── HERO ── */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2240] via-[#1E3A5F] to-[#0F2240]" />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #F59E0B22 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F622 0%, transparent 50%)' }} />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/20 backdrop-blur-sm">
              <Shield size={11} />
              SOC 2 Ready · Multi-Tenant · Enterprise Grade
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-6">
              AP automation built for<br />
              <span className="text-amber-400">finance teams</span> that<br />
              can't afford mistakes
            </h1>
            <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-10">
              LedgerPay gives AP clerks a structured invoice queue, CFOs a real-time authorization gate, and everyone a complete audit trail — all wired to Stripe for same-day payment execution.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/sign-up-login-screen" className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-7 py-3.5 rounded-xl transition-all duration-200 text-sm shadow-lg shadow-amber-500/30">
                Start 14-day free trial
                <ArrowRight size={16} />
              </Link>
              <Link href="/pricing" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 text-sm border border-white/20 backdrop-blur-sm">
                View pricing
              </Link>
            </div>
            <p className="text-xs text-white/40 mt-4">No credit card required · Cancel anytime · 2-minute setup</p>
          </div>

          {/* Dashboard preview card */}
          <div className="mt-16 relative">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-amber-400/60" />
                <div className="w-3 h-3 rounded-full bg-green-400/60" />
                <div className="flex-1 bg-white/5 rounded-md h-5 mx-4" />
              </div>
              {/* Mock dashboard rows */}
              <div className="space-y-2">
                {[
                  { vendor: 'Apex Logistics Inc.', amount: '$24,500', status: 'Pending Auth', priority: 'critical' },
                  { vendor: 'CloudStack Services', amount: '$8,200', status: 'Authorized', priority: 'medium' },
                  { vendor: 'Meridian Supplies Co.', amount: '$3,750', status: 'Processing', priority: 'low' },
                  { vendor: 'TechBridge Solutions', amount: '$61,000', status: 'Pending Auth', priority: 'high' },
                ]?.map((row, i) => (
                  <div key={`row-${i}`} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2.5">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row?.priority === 'critical' ? 'bg-red-400' : row?.priority === 'high' ? 'bg-amber-400' : row?.priority === 'medium' ? 'bg-blue-400' : 'bg-slate-400'}`} />
                    <span className="text-white/80 text-xs font-medium flex-1">{row?.vendor}</span>
                    <span className="text-white/60 text-xs font-tabular">{row?.amount}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row?.status === 'Authorized' ? 'bg-emerald-500/20 text-emerald-300' : row?.status === 'Processing' ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      {row?.status}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between px-1">
                <span className="text-white/30 text-xs">4 invoices · $97,450 total exposure</span>
                <span className="text-white/30 text-xs">Updated 2 min ago</span>
              </div>
            </div>
            {/* Glow */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-amber-500/20 blur-2xl rounded-full" />
          </div>
        </div>
      </section>
      {/* ── LOGOS / SOCIAL PROOF BAR ── */}
      <section className="py-10 border-y border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-6">Trusted by finance teams at</p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-50">
            {['Meridian Holdings', 'Vantage Capital', 'Clearstone Partners', 'Apex Ventures', 'NorthBridge Group']?.map((name) => (
              <span key={name} className="text-sm font-bold text-muted-foreground tracking-tight">{name}</span>
            ))}
          </div>
        </div>
      </section>
      {/* ── FEATURES BENTO ── */}
      <section id="features" className="py-24 max-w-6xl mx-auto px-6">
        <div className="mb-14">
          <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-primary/20">
            <Zap size={11} />
            Built for AP Clerks, Bookkeepers & CFOs
          </div>
          <h2 className="text-4xl font-bold text-foreground tracking-tight max-w-xl">
            Every control your finance team needs, nothing they don't
          </h2>
        </div>

        {/* Bento grid — asymmetric */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Large card — AP Ledger */}
          <div className="md:col-span-7 bg-card border border-border rounded-2xl p-7 relative overflow-hidden group hover:border-primary/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <FileText size={20} className="text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Structured AP Ledger</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-sm">
              Every invoice enters a prioritized queue with due-date urgency scoring, vendor tier classification, and one-click authorization routing to the CFO.
            </p>
            <div className="space-y-2">
              {['Priority-ranked invoice queue', 'Vendor tier & risk classification', 'Bulk authorization workflows', 'Real-time status tracking']?.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 size={14} className="text-primary flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          </div>

          {/* Tall card — CFO Dashboard */}
          <div className="md:col-span-5 bg-gradient-to-br from-primary to-[#0F2240] rounded-2xl p-7 text-white relative overflow-hidden group hover:shadow-lg hover:shadow-primary/20 transition-all duration-300">
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/5 rounded-full translate-x-1/2 translate-y-1/2" />
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center mb-5">
              <TrendingUp size={20} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">CFO Command Center</h3>
            <p className="text-sm text-white/70 leading-relaxed mb-5">
              Cash runway forecasting, AP aging analysis, and liquidity projections — updated in real time as invoices move through the queue.
            </p>
            <div className="bg-white/10 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-white/60 font-semibold uppercase tracking-wide">Cash Runway</span>
                <span className="text-xs text-emerald-300 font-bold">+12 days</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">47 days</div>
              <div className="w-full bg-white/10 rounded-full h-1.5 mt-3">
                <div className="bg-amber-400 h-1.5 rounded-full" style={{ width: '62%' }} />
              </div>
            </div>
          </div>

          {/* Medium card — Policy Engine */}
          <div className="md:col-span-4 bg-card border border-border rounded-2xl p-7 group hover:border-primary/30 transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
              <Shield size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Policy Engine</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Set per-vendor spending limits, dual-approval thresholds, and payment hold rules. Enforced at the database level — not just the UI.
            </p>
          </div>

          {/* Medium card — Audit Trail */}
          <div className="md:col-span-4 bg-card border border-border rounded-2xl p-7 group hover:border-primary/30 transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-5">
              <Lock size={20} className="text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Immutable Audit Trail</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Every authorization, rejection, and payment is timestamped and signed. Exportable for auditors, accountants, and compliance reviews.
            </p>
          </div>

          {/* Medium card — Team Roles */}
          <div className="md:col-span-4 bg-card border border-border rounded-2xl p-7 group hover:border-primary/30 transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
              <Users size={20} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Role-Based Access</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              AP Clerk, AP Manager, Controller, and CFO roles with scoped permissions. Invite your whole team in under 2 minutes.
            </p>
          </div>

          {/* Wide card — Stripe Integration */}
          <div className="md:col-span-8 bg-card border border-border rounded-2xl p-7 relative overflow-hidden group hover:border-primary/30 transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-5">
              <CreditCard size={20} className="text-violet-600" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Stripe-Powered Payment Execution</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
              Once a CFO authorizes an invoice, LedgerPay creates a Stripe PaymentIntent server-side and executes the payment — with the gateway transaction ID written back to the audit log automatically.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {['ACH Transfers', 'Wire Payments', 'Card Payments', 'Real-time Status Sync', 'Webhook Reconciliation']?.map((tag) => (
                <span key={tag} className="text-xs font-semibold bg-muted text-muted-foreground px-3 py-1 rounded-full border border-border">{tag}</span>
              ))}
            </div>
          </div>

          {/* Small card — Notifications */}
          <div className="md:col-span-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl p-7 group hover:shadow-md transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-5">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Smart Alerts</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Invoice due-date alerts, overdue notices, and payment confirmation emails — delivered automatically via Resend.
            </p>
          </div>
        </div>
      </section>
      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-24 bg-muted/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-primary/20">
              <Star size={11} fill="currentColor" />
              Customer Stories
            </div>
            <h2 className="text-4xl font-bold text-foreground tracking-tight">
              Finance teams that switched to LedgerPay
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials?.map((t, i) => (
              <div key={`testimonial-${i}`} className="bg-card border border-border rounded-2xl p-7 flex flex-col relative group hover:border-primary/30 hover:shadow-md transition-all duration-300">
                <Quote size={28} className="text-primary/20 mb-4 flex-shrink-0" />
                <p className="text-sm text-foreground leading-relaxed flex-1 mb-6">"{t?.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className={`w-9 h-9 rounded-full ${t?.color} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-xs font-bold text-white">{t?.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t?.name}</p>
                    <p className="text-xs text-muted-foreground">{t?.title} · {t?.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── PRICING CTA ── */}
      <section className="py-24 max-w-6xl mx-auto px-6">
        <div className="bg-gradient-to-br from-primary via-[#1a3356] to-[#0F2240] rounded-3xl p-12 md:p-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/20">
              <Clock size={11} />
              14-day free trial · No credit card required
            </div>
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Ready to close your AP cycle in days, not weeks?
            </h2>
            <p className="text-white/70 text-lg mb-8 leading-relaxed">
              Start with the full Growth plan free for 14 days. Invite your team, import your vendors, and process your first authorized payment — all before you pay a cent.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/sign-up-login-screen" className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 text-sm shadow-lg shadow-amber-500/30">
                Start free trial
                <ArrowRight size={16} />
              </Link>
              <Link href="/pricing" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 text-sm border border-white/20">
                Compare plans
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-4">
              {['SOC 2 Ready', 'Multi-tenant isolation', 'Stripe-powered payments', 'Immutable audit logs']?.map((badge) => (
                <div key={badge} className="flex items-center gap-1.5 text-white/60 text-xs">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  {badge}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* ── FAQ ── */}
      <section id="faq" className="py-24 border-t border-border bg-muted/20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-foreground tracking-tight mb-3">Frequently asked questions</h2>
            <p className="text-muted-foreground">Everything you need to know before you sign up.</p>
          </div>
          <div className="space-y-3">
            {faqs?.map((faq, i) => (
              <div key={`faq-${i}`} className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground pr-4">{faq?.q}</span>
                  <ChevronDown
                    size={16}
                    className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed pt-4">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-12 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8">
            <div className="max-w-xs">
              <div className="flex items-center gap-2 mb-3">
                <AppLogo size={24} />
                <span className="font-bold text-sm text-foreground">LedgerPay</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Intelligent AP automation for finance teams that need speed, control, and a complete audit trail.
              </p>
            </div>
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-widest mb-3">Product</p>
                <div className="space-y-2">
                  <Link href="/pricing" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
                  <a href="#features" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
                  <Link href="/sign-up-login-screen" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Sign up</Link>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-widest mb-3">Company</p>
                <div className="space-y-2">
                  <a href="#testimonials" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Customers</a>
                  <a href="#faq" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-widest mb-3">Legal</p>
                <div className="space-y-2">
                  <Link href="/terms" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
                  <Link href="/privacy" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">© 2026 LedgerPay. All rights reserved.</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield size={11} />
              SOC 2 Ready · Data encrypted at rest and in transit
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
